import React, { useState } from "react";
import {
  Segment,
  Grid,
  Header,
  Icon,
  Input,
  Button,
  Form,
  Message,
  Modal,
} from "semantic-ui-react";
import { Formik } from "formik";
import * as Yup from "yup";
import { normalizeId } from "../../../../../../formatting/inventoryIds";
import axios from "../../../../../../../apis/dvdlocator";
import ComponentNumberEdit from "./ComponentNumberEdit"; // Import the ComponentNumberEdit component

// Validation schema for the title field
const titleValidationSchema = Yup.object({
  inventory_title: Yup.string().required("Title is required"),
});

const updateTitleAPI = async (inventory_id, newTitle) => {
  return axios.put(`/inventory/${inventory_id}`, { title: newTitle });
};

const InventorySection = ({
  inventory,
  setInventory,
  title,
  setTitle,
  isTitleEditing,
  setIsTitleEditing,
  supplyUnits,
  startWizard,
  onTitleUpdate,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isComponentEditModalOpen, setIsComponentEditModalOpen] =
    useState(false);

  // Handle title form submission - to use with Formik
  const handleTitleEdit = async (values, { setSubmitting }) => {
    // Skip submission if title hasn't changed
    if (values.inventory_title === title) {
      setIsTitleEditing(false);
      setSubmitting(false);
      return;
    }

    try {
      setApiError(null);
      setIsSubmitting(true);

      // Call the API
      await updateTitleAPI(inventory.inventory_id, values.inventory_title);

      // Update local state
      setTitle(values.inventory_title);
      setIsTitleEditing(false);

      // Notify parent component
      if (onTitleUpdate) {
        onTitleUpdate(values.inventory_title);
      }
    } catch (error) {
      console.error("Error updating title:", error);
      setApiError(error.message || "Failed to update title");
      // Keep editing mode active when there's an error
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  // Clear error when user starts editing again
  const handleStartEditing = () => {
    setApiError(null);
    setIsTitleEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setApiError(null);
    setIsTitleEditing(false);
  };

  // Open component edit modal
  const openComponentEditModal = () => {
    setIsComponentEditModalOpen(true);
  };

  // Close component edit modal
  const closeComponentEditModal = () => {
    setIsComponentEditModalOpen(false);
  };

  return (
    <Segment secondary style={{ marginBottom: "20px", marginTop: "20px" }}>
      <Grid>
        <Grid.Row>
          <Grid.Column width={8}>
            {isTitleEditing ? (
              <Formik
                initialValues={{ inventory_title: title }}
                validationSchema={titleValidationSchema}
                onSubmit={handleTitleEdit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleSubmit,
                  handleChange,
                  isSubmitting: formikSubmitting,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <Input
                          fluid
                          name="inventory_title"
                          value={values.inventory_title}
                          onChange={handleChange}
                          error={
                            touched.inventory_title && errors.inventory_title
                              ? true
                              : false
                          }
                          disabled={isSubmitting}
                        />
                        {touched.inventory_title && errors.inventory_title && (
                          <div style={{ color: "red", marginTop: "5px" }}>
                            {errors.inventory_title}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          marginLeft: "8px",
                          display: "flex",
                          gap: "5px",
                        }}
                      >
                        <Button
                          icon
                          type="submit"
                          color="green"
                          size="small"
                          disabled={
                            isSubmitting ||
                            formikSubmitting ||
                            (touched.inventory_title &&
                              errors.inventory_title) ||
                            values.inventory_title.trim() === title.trim()
                          }
                          loading={isSubmitting}
                        >
                          <Icon name="check" />
                        </Button>
                        <Button
                          icon
                          type="button"
                          color="red"
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={isSubmitting}
                        >
                          <Icon name="x" />
                        </Button>
                      </div>
                    </div>

                    {apiError && (
                      <Message
                        negative
                        size="tiny"
                        style={{ marginTop: "10px" }}
                      >
                        <Icon name="exclamation triangle" />
                        {apiError}
                      </Message>
                    )}
                  </Form>
                )}
              </Formik>
            ) : (
              <div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Header as="h3" style={{ margin: 0 }}>
                    {title}
                  </Header>
                  <Button
                    icon="edit"
                    size="tiny"
                    style={{
                      marginLeft: "10px",
                      minWidth: "unset",
                      width: "32px",
                      height: "32px",
                      padding: 0,
                    }}
                    onClick={handleStartEditing}
                  />
                </div>
                {apiError && (
                  <Message negative size="tiny" style={{ marginTop: "5px" }}>
                    <Icon name="exclamation triangle" />
                    {apiError}
                  </Message>
                )}
              </div>
            )}
            <p style={{ marginTop: "10px" }}>
              Shelf: {inventory?.inventory_shelf}
            </p>
            <p>Current Inv: {inventory?.inv}</p>
          </Grid.Column>
          <Grid.Column width={8}>
            <Header as="h3">
              Inventory ID: {normalizeId(inventory?.inventory_id)}
            </Header>
            <p>
              Part Number: {inventory?.part_number}{" "}
              <Button
                icon="edit"
                size="tiny"
                style={{
                  marginLeft: "10px",
                  minWidth: "unset",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                }}
                onClick={openComponentEditModal}
              />
            </p>
            <p style={{ display: "flex", alignItems: "center" }}>
              {supplyUnits.length} equivalent UPCs
              <Button
                size="tiny"
                icon
                labelPosition="right"
                color="blue"
                style={{ marginLeft: "15px" }}
                onClick={startWizard}
                disabled={supplyUnits.length < 2}
              >
                Split Equivalents
                <Icon name="object ungroup" />
              </Button>
            </p>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Component Number Edit Modal */}
      <Modal
        open={isComponentEditModalOpen}
        onClose={closeComponentEditModal}
        size="large"
      >
        <ComponentNumberEdit
          setInventory={setInventory}
          inventoryId={inventory?.inventory_id}
          partNumber={inventory?.part_number}
          supplyUnits={supplyUnits}
          onClose={closeComponentEditModal}
        />
      </Modal>
    </Segment>
  );
};

export default InventorySection;
